import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import {
  NATURE_OF_ISSUE,
  RECEIVED_DOCUMENTS,
  RECEIVED_MODE,
  ACKNOWLEDGE_SHARED_STATUS,
  STATUS,
  MOBILE_NUMBER,
  MOBILE_COUNTRY_CODE,
  STATUS_DATE,
  EMAIL_ID,
  EMPLOYEE_STATUS
} from 'src/app/constants/field-id-constants'
import { GlobalConstants } from 'src/app/constants/global-constants'
import {
  CodeLookup,
  OfflineTrackerAPIModel,
  StatusAPIModel,
  StatusHistoryAPIModel,
  TaskTypeAPIModel
} from 'src/app/models/api.model'
import { CommonService, formatDatetoYYYYMMDD } from 'src/app/services/common/common.service'
import { OptionsService } from 'src/app/services/common/options.service'
import { OfflineTrackerService } from 'src/app/services/offline-tracker/offline-tracker.service'
import countryCodes from '../../../../assets/other/country-code.json'
import { InitializeService } from 'src/app/services/initialize/initialize.service'
import { getOptionsFromState } from 'src/app/store/selector/initialize.selector'
import { AppState } from 'src/app/store/state/app.state'
import { Store } from '@ngrx/store'
import { validatePFNumber, validateUAN } from '../../import/validators/validator.functions'
import { emailValidatorForNonMandatory, futureDateValidator } from 'src/app/validators/validators.validator'
import { filter, Subscription } from 'rxjs'

@Component({
  selector: 'app-offline-tracker-details',
  templateUrl: './offline-tracker-details.component.html',
  styleUrls: ['./offline-tracker-details.component.scss']
})
export class OfflineTrackerDetailsComponent implements OnInit, OnDestroy {
  private id: string
  countryCodes: { dialCode: string }[] = countryCodes
  trackerDetailsForm: FormGroup
  offlineTracker: OfflineTrackerAPIModel
  hasFormChanged: boolean
  isOfflineTrackerUnlocked: boolean = false
  offlineTrackerDetails: OfflineTrackerAPIModel
  acknowledgeShareStatusOptionsList: CodeLookup[] = []
  requestCompletionStatusOptionsList: CodeLookup[] = []
  employeeStatusOptionsList: CodeLookup[] = []
  isAcknowledgeSharedStatusMandatory: boolean = false
  isStatusDateRequired: boolean = false
  selectTaskTypesSubscription$: Subscription
  taskTypes: TaskTypeAPIModel[]
  multiDropdownOptions: {
    NATURE_OF_ISSUE: { list: CodeLookup[]; selectedOptions: any[] }
    RECEIVED_MODE: { list: CodeLookup[]; selectedOptions: any[] }
    RECEIVED_DOCUMENTS: { list: CodeLookup[]; selectedOptions: any[] }
  } = {
    NATURE_OF_ISSUE: {
      list: [],
      selectedOptions: []
    },
    RECEIVED_MODE: {
      list: [],
      selectedOptions: []
    },
    RECEIVED_DOCUMENTS: {
      list: [],
      selectedOptions: []
    }
  }
  statusHistory: StatusHistoryAPIModel[]
  trackerDetailsFormStatus$: Subscription
  trackerDetailsFormMobileNumber$: Subscription
  getOptionsFromState$: Subscription
  getDetailsFromStore$: Subscription
  private readonly emailIdValidators = [
    emailValidatorForNonMandatory(),
    Validators.minLength(3),
    Validators.maxLength(70)
  ]
  isEmailIdMandatory: boolean = false
  readonly MODULENAME = GlobalConstants.MODULE.OFFLINE_TRACKER
  statusListFromSelector: StatusAPIModel[]

  constructor(
    private offlineTrackerService: OfflineTrackerService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private cs: CommonService,
    private optionsService: OptionsService,
    private appState: Store<AppState>,
    private initializeService: InitializeService
  ) {}

  ngOnInit() {
    this.buildTrackerDetailsForm()
    this.route.params.subscribe(async params => {
      const id = params['id']
      this.id = id
      this.dispatchFetchOfflineTrackerDetailsAction(id)
      await this.populateOptions()
      this.getOTDetailsFromStore()
    })
  }

  private buildTrackerDetailsForm() {
    this.trackerDetailsForm = this.formBuilder.group({
      companyEntityId: ['', Validators.required],
      employeeCode: ['', [Validators.minLength(3), Validators.maxLength(40)]],
      employeeStatus: ['', Validators.required],
      firstName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      middleName: ['', [Validators.minLength(3), Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      uanNumber: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      memberId: [
        '',
        [Validators.required, Validators.minLength(21), Validators.maxLength(21), Validators.pattern('^[a-zA-Z0-9]+$')]
      ],
      [NATURE_OF_ISSUE]: this.formBuilder.array([], [Validators.required, Validators.minLength(1)]),
      additionalDetails: ['', [Validators.minLength(3), Validators.maxLength(100)]],
      [RECEIVED_DOCUMENTS]: this.formBuilder.array([], [Validators.required, Validators.minLength(1)]),
      [MOBILE_COUNTRY_CODE]: ['+91', [Validators.maxLength(4)]],
      [MOBILE_NUMBER]: ['', [Validators.pattern('^[0-9]{10}$')]],
      [EMAIL_ID]: ['', this.emailIdValidators],
      [RECEIVED_MODE]: this.formBuilder.array([], [Validators.required, Validators.minLength(1)]),
      receivedDate: [''],
      receivedMonth: [''],
      [STATUS]: ['', Validators.required],
      [STATUS_DATE]: [''],
      [ACKNOWLEDGE_SHARED_STATUS]: ['', Validators.required],
      requestCompletionStatus: ['', Validators.required],
      remarks: ['', [(Validators.minLength(3), Validators.maxLength(300))]]
    })
    this.updateEmailIdFieldBasedOnEmployeeStatus()
  }
  get f() {
    return this.trackerDetailsForm.controls
  }

  private dispatchFetchOfflineTrackerDetailsAction(id: string) {
    this.offlineTrackerService.dispatchFetchDetailsAction(id)
  }

  updateOfflineTracker() {
    if (this.trackerDetailsForm.invalid) {
      this.trackerDetailsForm.markAllAsTouched()
      return
    }
    this.callUpdateAPI()
  }

  callUpdateAPI(): void {
    const formData = { ...this.trackerDetailsForm.value }
    if (formData.mobileNumber) {
      formData.mobileNumber = formData.mobileCountryCode.slice(1) + ' ' + formData.mobileNumber
    }
    this.offlineTrackerService.dispatchUpdateAction(
      this.id,
      formData,
      this.cs.createRoutingURL([GlobalConstants.ROUTING.OFFLINE_TRACKER, GlobalConstants.ROUTING.SUMMARY])
    )
  }

  private getOTDetailsFromStore() {
    this.getDetailsFromStore$ = this.offlineTrackerService.getDetailsFromStore().subscribe(ot => {
      if (ot) {
        this.offlineTrackerDetails = ot
        this.populateMultiDropdownOptions(ot)
        this.trackerDetailsForm.patchValue(ot)
        this.trackerDetailsForm.patchValue({ statusDate: formatDatetoYYYYMMDD(ot.statusDate) })
        if (ot.mobileNumber) {
          const mobileSplit = ot.mobileNumber.split(' ')
          this.trackerDetailsForm.patchValue({
            mobileCountryCode: mobileSplit[0],
            mobileNumber: mobileSplit[1]
          })
        }
      }
    })
  }

  private populateMultiDropdownOptions(ot: OfflineTrackerAPIModel) {
    this.multiDropdownOptions.NATURE_OF_ISSUE.selectedOptions = this.optionsService.filterCodeLookupsByKey2(
      this.multiDropdownOptions?.NATURE_OF_ISSUE?.list,
      ot?.natureOfIssue
    )
    this.multiDropdownOptions.RECEIVED_DOCUMENTS.selectedOptions = this.optionsService.filterCodeLookupsByKey2(
      this.multiDropdownOptions?.RECEIVED_DOCUMENTS?.list,
      ot?.receivedDocuments
    )
    this.multiDropdownOptions.RECEIVED_MODE.selectedOptions = this.optionsService.filterCodeLookupsByKey2(
      this.multiDropdownOptions?.RECEIVED_MODE?.list,
      ot?.receivedMode
    )
    this.onMultiSelectChange(this.multiDropdownOptions.NATURE_OF_ISSUE.selectedOptions, NATURE_OF_ISSUE)
    this.onMultiSelectChange(this.multiDropdownOptions.RECEIVED_DOCUMENTS.selectedOptions, RECEIVED_DOCUMENTS)
    this.onMultiSelectChange(this.multiDropdownOptions.RECEIVED_MODE.selectedOptions, RECEIVED_MODE)
  }

  populateOptions(): Promise<void> {
    return new Promise(resolve => {
      this.getOptionsFromState$ = this.appState.select(getOptionsFromState).subscribe(options => {
        if (options) {
          this.acknowledgeShareStatusOptionsList = options[GlobalConstants.OPTION_TYPE.ACKNOWLEDGE_SHARE_STATUS]
          this.requestCompletionStatusOptionsList = options[GlobalConstants.OPTION_TYPE.REQUEST_COMPLETION_STATUS]
          this.multiDropdownOptions.RECEIVED_MODE.list = options[GlobalConstants.OPTION_TYPE.RECEIVED_MODE]
          this.multiDropdownOptions.RECEIVED_DOCUMENTS.list = options[GlobalConstants.OPTION_TYPE.RECEIVED_DOCUMENTS]
          this.multiDropdownOptions.NATURE_OF_ISSUE.list = options[GlobalConstants.OPTION_TYPE.NATURE_OF_ISSUE]
          this.employeeStatusOptionsList = options[GlobalConstants.OPTION_TYPE.EMPLOYEE_STATUS]
          resolve()
        }
      })
    })
  }

  onMultiSelectChange(value: { key1: string; key2: string }[], field: string) {
    const formArray = this.trackerDetailsForm.get(field) as FormArray
    formArray.clear()
    value.forEach(v => formArray.push(this.formBuilder.control(v.key2)))
  }

  onNewOptionAdded(newOption: CodeLookup, field: string) {
    if (field === NATURE_OF_ISSUE) {
      this.multiDropdownOptions.NATURE_OF_ISSUE.list.push(newOption)
    }
    if (field === RECEIVED_MODE) {
      this.multiDropdownOptions.RECEIVED_MODE.list.push(newOption)
    }
    if (field === RECEIVED_DOCUMENTS) {
      this.multiDropdownOptions.RECEIVED_DOCUMENTS.list.push(newOption)
    }
  }

  validateUAN(event: any) {
    validateUAN(event)
  }

  validatePFNumber(event: any) {
    validatePFNumber(event)
  }

  exit() {
    this.cs.route(GlobalConstants.ROUTING.OFFLINE_TRACKER, GlobalConstants.ROUTING.SUMMARY)
  }

  onStatusSelected(status: StatusAPIModel) {
    this.trackerDetailsForm.controls['status'].setValue(status.code)

    this.isAcknowledgeSharedStatusMandatory = ['SUBMITTED', 'ONLINE_COMPLETED'].includes(status.code)

    if (this.isAcknowledgeSharedStatusMandatory) {
      this.trackerDetailsForm.get(ACKNOWLEDGE_SHARED_STATUS)?.setValidators(Validators.required)
    } else {
      this.trackerDetailsForm.get(ACKNOWLEDGE_SHARED_STATUS)?.clearValidators()
    }

    this.trackerDetailsForm.get(ACKNOWLEDGE_SHARED_STATUS)?.updateValueAndValidity()

    if (this.offlineTrackerDetails.status !== status.code) {
      this.isStatusDateRequired = true
      this.trackerDetailsForm.get(STATUS_DATE)?.setValidators([Validators.required, futureDateValidator()])
    } else {
      this.isStatusDateRequired = false
      this.trackerDetailsForm.get(STATUS_DATE)?.clearValidators()
      this.trackerDetailsForm.get(STATUS_DATE)?.setValidators([futureDateValidator()])
    }

    this.trackerDetailsForm.get(STATUS_DATE)?.updateValueAndValidity()

    this.trackerDetailsFormMobileNumber$ = this.trackerDetailsForm.get(MOBILE_NUMBER)?.valueChanges.subscribe(value => {
      if (value) {
        this.trackerDetailsForm.get(MOBILE_COUNTRY_CODE)?.setValidators(Validators.required)
      } else {
        this.trackerDetailsForm.get(MOBILE_COUNTRY_CODE)?.clearValidators()
      }
      this.trackerDetailsForm.get(MOBILE_COUNTRY_CODE)?.updateValueAndValidity()
    })
  }

  private updateEmailIdFieldBasedOnEmployeeStatus() {
    this.trackerDetailsForm
      .get(EMPLOYEE_STATUS)
      ?.valueChanges.pipe(filter(value => !!value))
      .subscribe(value => {
        const emailIdControl = this.trackerDetailsForm.get(EMAIL_ID)
        if (value === 'ACTIVE') {
          emailIdControl.clearValidators()
          this.trackerDetailsForm.get(EMAIL_ID).setValidators(this.emailIdValidators)
          this.isEmailIdMandatory = false
        } else {
          emailIdControl.clearValidators()
          emailIdControl?.setValidators([...this.emailIdValidators, Validators.required])
          this.isEmailIdMandatory = true
        }
        emailIdControl.updateValueAndValidity()
      })
  }

  onStatusListReceived(list: StatusAPIModel[]) {
    this.statusListFromSelector = list
    if (this.statusListFromSelector) {
      if (this.offlineTrackerDetails?.statusHistory) {
        this.statusHistory = []
        this.offlineTrackerDetails?.statusHistory.forEach(_ => {
          const status = this.statusListFromSelector?.find(status => status.code === _.status)
          const statusHistory: StatusHistoryAPIModel = { status: status?.label, recordedAt: _.recordedAt }
          this.statusHistory.push(statusHistory)
        })
      }
    }
  }

  onStatusChange(flag: boolean) {
  }

  ngOnDestroy(): void {
    this.selectTaskTypesSubscription$?.unsubscribe?.()
    this.trackerDetailsFormStatus$?.unsubscribe?.()
    this.trackerDetailsFormMobileNumber$?.unsubscribe?.()
    this.getOptionsFromState$?.unsubscribe?.()
    this.getDetailsFromStore$?.unsubscribe?.()
  }
}
